import { Controller } from "@hotwired/stimulus"

// Webshop-style upscale viewer for the model show-page hero.
//
//   ┌───────────────────────────────┬──────────────────┐
//   │                               │   ORIGINAL crop  │
//   │   HERO (upscaled output)      │   100%, synced   │
//   │   ┌──┐  rect indicator        │   to cursor      │
//   │   └──┘  matches crop region   ├──────────────────┤
//   │                               │   UPSCALED crop  │
//   │                               │   ×N, synced     │
//   └───────────────────────────────┴──────────────────┘
//   [thumb] [thumb] [thumb] …  ← click to switch active pair
//
// Hero shows the OUTPUT (the "product" in webshop terms). On hover the
// right column reveals two crops at the cursor's source-pixel coord:
// the original (CSS-upscaled to output dims = soft) and the upscaled
// (native pixels = sharp). The indicator on the hero is a rectangle
// sized to the crop region so the user sees what slice they're peeking.
//
// Mobile / touch: same showcase, but the overlay reflows below the
// hero (CSS-only) and finger drag on the hero drives the crops the
// same way mouse does on desktop. We preventDefault on touchstart so
// the page doesn't scroll while the user is comparing.
//
// Targets:
//   hero        — the cursor-tracking surface (carries the output <img>)
//   heroImg     — <img> showing the output
//   indicator   — sized rectangle marking the crop region on the hero
//   overlay     — peek overlay container (fades in on .is-peeking)
//   cropOrig    — original crop lens (background-image + bg-position)
//   cropUp      — upscaled crop lens
//   factor      — scale-factor pill in the upscaled lens (text node at
//                 [data-ratio-num])
//   frame       — filmstrip frames; data-pair-index → pairsValue idx
//
// Values:
//   pairs — [{ inputUrl, outputUrl, scaleFactor, inputW, inputH,
//             outputW, outputH }]
//           Both lens backgrounds use the OUTPUT's native dims as
//           background-size — that's what makes the original lens
//           render with real interpolation softness while the upscaled
//           lens shows true 100%.
export default class UpscaleShowcaseController extends Controller {
  static targets = ["hero", "heroImg", "indicator", "overlay", "cropOrig", "cropUp", "factor", "frame"]
  static values  = { pairs: Array }

  declare heroTarget: HTMLElement
  declare heroImgTarget: HTMLImageElement
  declare indicatorTarget: HTMLElement
  declare overlayTarget: HTMLElement
  declare cropOrigTarget: HTMLElement
  declare cropUpTarget: HTMLElement
  declare factorTarget: HTMLElement
  declare hasFactorTarget: boolean
  declare frameTargets: HTMLElement[]
  declare pairsValue: Array<{
    inputUrl: string
    outputUrl: string
    scaleFactor: number | null
    inputW: number
    inputH: number
    outputW: number
    outputH: number
  }>

  private currentPair = 0
  // Fingerprint of URLs we last assigned to lens backgrounds, so a pair
  // switch forces them to repaint instead of keeping yesterday's image.
  private mountedFingerprint = ""

  connect() {
    this.heroTarget.style.touchAction = "none"
    this.applyPair(0)

    this.heroTarget.addEventListener("mousemove",  this.handleMouseMove)
    this.heroTarget.addEventListener("mouseleave", this.handleMouseLeave)
    this.heroTarget.addEventListener("touchstart", this.handleTouchStart, { passive: false })
  }

  disconnect() {
    this.heroTarget.removeEventListener("mousemove",  this.handleMouseMove)
    this.heroTarget.removeEventListener("mouseleave", this.handleMouseLeave)
    this.heroTarget.removeEventListener("touchstart", this.handleTouchStart)
    this.stopTouchDrag()
    this.deactivate()
  }

  setPair(event: Event) {
    const idx = Number((event.currentTarget as HTMLElement).dataset.pairIndex ?? -1)
    if (Number.isFinite(idx)) this.applyPair(idx)
  }

  private applyPair(idx: number) {
    if (idx < 0 || idx >= this.pairsValue.length) return
    this.currentPair = idx
    const pair = this.pairsValue[idx]

    // Hero shows the upscaled output (the "product").
    this.heroImgTarget.src = pair.outputUrl

    const fingerprint = `${pair.inputUrl}|${pair.outputUrl}`
    if (fingerprint !== this.mountedFingerprint) {
      this.cropOrigTarget.style.backgroundImage    = `url(${JSON.stringify(pair.inputUrl)})`
      this.cropUpTarget.style.backgroundImage      = `url(${JSON.stringify(pair.outputUrl)})`
      this.cropOrigTarget.style.backgroundRepeat   = "no-repeat"
      this.cropUpTarget.style.backgroundRepeat     = "no-repeat"
      this.cropOrigTarget.style.backgroundPosition = "center"
      this.cropUpTarget.style.backgroundPosition   = "center"
      this.cropOrigTarget.style.backgroundSize     = `${pair.outputW}px ${pair.outputH}px`
      this.cropUpTarget.style.backgroundSize       = `${pair.outputW}px ${pair.outputH}px`
      this.mountedFingerprint = fingerprint
    }

    if (this.hasFactorTarget) {
      const num = this.factorTarget.querySelector<HTMLElement>("[data-ratio-num]")
      if (num) num.textContent = pair.scaleFactor != null ? String(pair.scaleFactor) : ""
      this.factorTarget.toggleAttribute("hidden", pair.scaleFactor == null)
    }

    this.frameTargets.forEach((f, i) => f.classList.toggle("is-active", i === idx))
  }

  // ── Pointer ──

  private handleMouseMove = (e: MouseEvent) => {
    this.updateFromPoint(e.clientX, e.clientY)
    this.activate()
  }

  private handleMouseLeave = () => {
    this.deactivate()
  }

  private handleTouchStart = (e: TouchEvent) => {
    e.preventDefault()
    const t = e.touches[0]
    if (!t) return
    this.updateFromPoint(t.clientX, t.clientY)
    this.activate()
    document.addEventListener("touchmove",   this.handleDocTouchMove, { passive: false })
    document.addEventListener("touchend",    this.handleDocTouchEnd)
    document.addEventListener("touchcancel", this.handleDocTouchEnd)
  }

  private handleDocTouchMove = (e: TouchEvent) => {
    const t = e.touches[0]
    if (!t) return
    e.preventDefault()
    this.updateFromPoint(t.clientX, t.clientY)
  }

  private handleDocTouchEnd = () => {
    // Leave the overlay open at last position; the next touchstart
    // re-anchors it. A tap outside dismisses (no listener — natural).
    this.stopTouchDrag()
  }

  private stopTouchDrag() {
    document.removeEventListener("touchmove",   this.handleDocTouchMove)
    document.removeEventListener("touchend",    this.handleDocTouchEnd)
    document.removeEventListener("touchcancel", this.handleDocTouchEnd)
  }

  private activate() {
    if (this.element.classList.contains("is-peeking")) return
    this.element.classList.add("is-peeking")
    // Touch users can't trigger mouseleave to close the overlay; listen
    // for taps outside the hero and dismiss when one lands. Harmless on
    // desktop because mouseleave deactivates first.
    document.addEventListener("pointerdown", this.handleOutsidePointer, true)
  }

  private deactivate() {
    if (!this.element.classList.contains("is-peeking")) return
    this.element.classList.remove("is-peeking")
    document.removeEventListener("pointerdown", this.handleOutsidePointer, true)
  }

  private handleOutsidePointer = (e: Event) => {
    const target = e.target as Node | null
    if (target && this.heroTarget.contains(target)) return
    this.deactivate()
  }

  private updateFromPoint(clientX: number, clientY: number) {
    const rect = this.heroTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const nx = clamp((clientX - rect.left) / rect.width)
    const ny = clamp((clientY - rect.top)  / rect.height)
    const pair = this.pairsValue[this.currentPair]
    if (!pair) return

    // The hero <img> uses object-fit: cover on the OUTPUT image.
    // Translate cursor-on-rendered-frame coords into native-image coords
    // so the indicator and the crop view stay in sync with what the
    // user sees under their cursor.
    const triggerAspect = rect.width / rect.height
    const imageAspect   = pair.outputW / pair.outputH
    let visibleX = 1, visibleY = 1, cropX = 0, cropY = 0
    if (imageAspect > triggerAspect) {
      visibleX = triggerAspect / imageAspect
      cropX    = (1 - visibleX) / 2
    } else if (imageAspect < triggerAspect) {
      visibleY = imageAspect / triggerAspect
      cropY    = (1 - visibleY) / 2
    }
    const imgX = cropX + nx * visibleX
    const imgY = cropY + ny * visibleY

    this.positionLens(this.cropOrigTarget, imgX, imgY, pair.outputW, pair.outputH)
    this.positionLens(this.cropUpTarget,   imgX, imgY, pair.outputW, pair.outputH)

    // Indicator rectangle, sized to match the crop region. The lens
    // shows lensW × lensH pixels of the source; in rendered hero coords
    // that's (lensW / srcW) / visibleFraction × triggerW. Clamp to a
    // sane minimum so it stays visible at high zoom ratios.
    const lensW = this.cropOrigTarget.clientWidth
    const lensH = this.cropOrigTarget.clientHeight
    const MIN = 32
    const indW = Math.max(MIN, lensW && pair.outputW
      ? (lensW / pair.outputW) / visibleX * rect.width
      : MIN)
    const indH = Math.max(MIN, lensH && pair.outputH
      ? (lensH / pair.outputH) / visibleY * rect.height
      : MIN)
    const indX = nx * rect.width  - indW / 2
    const indY = ny * rect.height - indH / 2
    this.indicatorTarget.style.width     = `${indW}px`
    this.indicatorTarget.style.height    = `${indH}px`
    this.indicatorTarget.style.transform = `translate3d(${indX}px, ${indY}px, 0)`
  }

  private positionLens(lens: HTMLElement, imgX: number, imgY: number, srcW: number, srcH: number) {
    const w = lens.clientWidth
    const h = lens.clientHeight
    if (!w || !h) return
    const bgX = -(imgX * srcW - w / 2)
    const bgY = -(imgY * srcH - h / 2)
    lens.style.backgroundPosition = `${bgX}px ${bgY}px`
  }
}

const clamp = (v: number) => Math.max(0, Math.min(1, v))
